import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { 
  TagSelectorProps, 
  TagOption,
  MIN_SEARCH_LENGTH, 
  SEARCH_DEBOUNCE_DELAY, 
  MAX_SEARCH_RESULTS 
} from './config';
import './tag-selector.scss';

const TagSelector: React.FC<TagSelectorProps> = ({
  value,
  onChange,
  placeholder = "Search or add tags...",
  className = '',
  disabled = false
}) => {
  const supabase = useSupabaseClient();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<TagOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Search for tags
  const searchTags = useCallback(async (term: string) => {
    if (term.length < MIN_SEARCH_LENGTH) {
      setSearchResults([]);
      return;
    }

    setIsLoading(true);
    try {
      const { data, error } = await supabase.rpc('search_tags', {
        search_term: term,
        limit_results: MAX_SEARCH_RESULTS
      });

      if (error) throw error;

      // Filter out already selected tags
      const selectedIds = new Set(value.map(t => t.id));
      const filtered = (data || []).filter((tag: TagOption) => !selectedIds.has(tag.id));
      setSearchResults(filtered);
    } catch (err) {
      console.error('Error searching tags:', err);
      setSearchResults([]);
    } finally {
      setIsLoading(false);
    }
  }, [supabase, value]);

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchTerm.length >= MIN_SEARCH_LENGTH) {
      searchTimeoutRef.current = setTimeout(() => {
        searchTags(searchTerm);
      }, SEARCH_DEBOUNCE_DELAY);
    } else {
      setSearchResults([]);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchTerm, searchTags]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle selecting a tag from search results
  const handleSelectTag = (tag: TagOption) => {
    onChange([...value, tag]);
    setSearchTerm('');
    setSearchResults([]);
    inputRef.current?.focus();
  };

  // Handle removing a selected tag
  const handleRemoveTag = (tagId: number) => {
    onChange(value.filter(t => t.id !== tagId));
  };

  // Handle creating a new tag
  const handleCreateTag = async () => {
    const trimmedName = searchTerm.trim();
    if (!trimmedName || isCreating) return;

    // Check if tag already exists in selected
    if (value.some(t => t.name.toLowerCase() === trimmedName.toLowerCase())) {
      setSearchTerm('');
      return;
    }

    setIsCreating(true);
    try {
      // Get or create the tag
      const { data: tagId, error } = await supabase.rpc('get_or_create_tag', {
        p_tag_name: trimmedName
      });

      if (error) throw error;

      // Add to selected tags
      const newTag: TagOption = { id: tagId, name: trimmedName };
      onChange([...value, newTag]);
      setSearchTerm('');
      setSearchResults([]);
      inputRef.current?.focus();
    } catch (err) {
      console.error('Error creating tag:', err);
    } finally {
      setIsCreating(false);
    }
  };

  // Handle key press in input
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const trimmedSearch = searchTerm.trim();
      
      // If there are search results and search matches one exactly, select it
      const exactMatch = searchResults.find(
        t => t.name.toLowerCase() === trimmedSearch.toLowerCase()
      );
      
      if (exactMatch) {
        handleSelectTag(exactMatch);
      } else if (trimmedSearch.length >= MIN_SEARCH_LENGTH) {
        // Create new tag
        handleCreateTag();
      }
    } else if (e.key === 'Backspace' && searchTerm === '' && value.length > 0) {
      // Remove last tag when backspace is pressed on empty input
      handleRemoveTag(value[value.length - 1].id);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
    }
  };

  // Check if current search term can create a new tag
  const canCreateTag = () => {
    const trimmed = searchTerm.trim();
    if (trimmed.length < MIN_SEARCH_LENGTH) return false;
    
    // Check if exact match exists in results or selected
    const existsInResults = searchResults.some(
      t => t.name.toLowerCase() === trimmed.toLowerCase()
    );
    const existsInSelected = value.some(
      t => t.name.toLowerCase() === trimmed.toLowerCase()
    );
    
    return !existsInResults && !existsInSelected;
  };

  return (
    <div 
      className={`tag-selector ${className} ${disabled ? 'disabled' : ''}`} 
      ref={containerRef}
    >
      {/* Selected tags - above input */}
      {value.length > 0 && (
        <div className="tag-selector__tags">
          {value.map(tag => (
            <span key={tag.id} className="tag-selector__tag">
              {tag.name}
              {!disabled && (
                <button
                  type="button"
                  className="tag-selector__tag-remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveTag(tag.id);
                  }}
                  aria-label={`Remove ${tag.name}`}
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}
      
      {/* Search input - full width */}
      <div 
        className={`tag-selector__input-container ${isOpen ? 'focused' : ''}`}
        onClick={() => {
          if (!disabled) {
            setIsOpen(true);
            inputRef.current?.focus();
          }
        }}
      >
        <input
          ref={inputRef}
          type="text"
          className="tag-selector__input"
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
        />
      </div>

      {/* Dropdown */}
      {isOpen && !disabled && (
        <div className="tag-selector__dropdown">
          {/* Search hint */}
          {searchTerm.length > 0 && searchTerm.length < MIN_SEARCH_LENGTH && (
            <div className="tag-selector__hint">
              Type {MIN_SEARCH_LENGTH - searchTerm.length} more character{searchTerm.length === MIN_SEARCH_LENGTH - 1 ? '' : 's'} to search...
            </div>
          )}

          {/* Loading state */}
          {isLoading && (
            <div className="tag-selector__hint">Searching...</div>
          )}

          {/* Search results */}
          {!isLoading && searchResults.length > 0 && (
            <div className="tag-selector__results">
              {searchResults.map(tag => (
                <div
                  key={tag.id}
                  className="tag-selector__option"
                  onClick={() => handleSelectTag(tag)}
                >
                  {tag.name}
                </div>
              ))}
            </div>
          )}

          {/* Create new tag option */}
          {!isLoading && canCreateTag() && (
            <div
              className="tag-selector__option tag-selector__option--create"
              onClick={handleCreateTag}
            >
              {isCreating ? (
                'Creating...'
              ) : (
                <>
                  Create "<strong>{searchTerm.trim()}</strong>"
                </>
              )}
            </div>
          )}

          {/* No results message */}
          {!isLoading && 
           searchTerm.length >= MIN_SEARCH_LENGTH && 
           searchResults.length === 0 && 
           !canCreateTag() && (
            <div className="tag-selector__hint">
              No tags found
            </div>
          )}

          {/* Empty state when dropdown opens */}
          {searchTerm.length === 0 && value.length === 0 && (
            <div className="tag-selector__hint">
              Start typing to search or create tags
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TagSelector;

